import React from "react";
import { motion } from "framer-motion";
import { HeartPulse, Sprout, Brain, BookMarked, ShieldCheck, ArrowRight } from "lucide-react";
import { whatsappLink } from "@/config/site";

const areas = [
  {
    icon: HeartPulse,
    tag: "Destaque",
    title: "Área da Saúde",
    desc: "Formações dirigidas a profissionais e estudantes da área da saúde — competências técnicas, atualização e desenvolvimento de carreira.",
    items: ["Cuidados a Idosos & Dependentes", "Primeiros Socorros", "Cuidados Pediátricos"],
  },
  {
    icon: Sprout,
    tag: "Destaque",
    title: "Desenvolvimento Pessoal",
    desc: "Programas focados em soft skills, autoconhecimento e crescimento humano — para potenciar carreira e vida pessoal.",
    items: ["Inteligência Emocional", "Comunicação Eficaz", "Gestão de Stress", "Liderança & Motivação"],
  },
  {
    icon: Brain,
    tag: "Complementar",
    title: "Educação & Pedagogia",
    desc: "Formação para profissionais de educação, pais e cuidadores — métodos atualizados e práticos.",
    items: ["Apoio ao Estudo", "Métodos de Aprendizagem", "Gestão Comportamental"],
  },
  {
    icon: BookMarked,
    tag: "Complementar",
    title: "Línguas & Comunicação",
    desc: "Programas de inglês e comunicação adaptados ao mundo profissional, com base na metodologia Cambridge.",
    items: ["Inglês para Negócios", "Apresentações Eficazes", "Escrita Profissional"],
  },
];

export const Trainings = () => {
  return (
    <section id="formacoes" data-testid="trainings-section" className="section bg-white relative overflow-hidden">
      <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-bordeaux-50/70 blur-3xl" />
      <div className="container-tight relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-bordeaux-700 mb-4">Formações DGERT</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-900 leading-tight">
              Instituto de Formação <span className="italic text-bordeaux-700">— entidade certificada pela DGERT.</span>
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed max-w-2xl">
              Para além do acompanhamento académico, somos uma entidade formadora certificada pela DGERT.
              Oferecemos um conjunto diversificado de formações, com destaque para as áreas da
              <span className="font-semibold text-stone-900"> saúde</span> e do
              <span className="font-semibold text-stone-900"> desenvolvimento pessoal</span>.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {[
                "Entidade Certificada DGERT",
                "Formação Profissional",
                "Certificados emitidos",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy-50 border border-navy-100 text-navy-700 text-xs font-semibold">
                  <ShieldCheck size={14} /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {areas.map((a, i) => (
            <motion.article
              key={a.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
              data-testid={`training-card-${i}`}
              className="group relative p-8 sm:p-10 rounded-3xl border border-stone-100 bg-stone-50/70 hover:bg-white hover:border-bordeaux-200 transition-all hover:-translate-y-1 hover:shadow-[0_25px_60px_rgba(28,25,23,0.08)]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="h-14 w-14 rounded-2xl bg-bordeaux-700 text-white flex items-center justify-center group-hover:bg-navy-700 transition-colors">
                  <a.icon size={24} strokeWidth={1.5} />
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full ${
                  a.tag === "Destaque" ? "bg-bordeaux-700 text-white" : "bg-stone-100 text-stone-600"
                }`}>{a.tag}</span>
              </div>

              <h3 className="mt-6 font-display text-2xl sm:text-3xl text-stone-900">{a.title}</h3>
              <p className="mt-3 text-stone-600 leading-relaxed">{a.desc}</p>

              <ul className="mt-6 grid sm:grid-cols-2 gap-x-4 gap-y-2">
                {a.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-stone-700">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-bordeaux-700 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-navy-700 text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/70 font-semibold mb-2">Formações personalizadas</div>
            <div className="font-display text-2xl sm:text-3xl leading-tight">
              Procura uma formação específica para a sua equipa ou instituição?
            </div>
            <p className="mt-3 text-white/80 text-sm sm:text-base">
              Desenhamos formações à medida para empresas, IPSS, lares e clínicas.
            </p>
          </div>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trainings-cta-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-navy-700 font-semibold hover:bg-bordeaux-50 transition-all hover:-translate-y-0.5 shrink-0"
          >
            Pedir informações <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};
